import { useCallback, useEffect, useMemo, useState } from "react";

import {
    NovedadesExportService,
    NovedadesExportServiceModel,
} from "@services/NovedadesExportService";

export interface ExportNovedadesEventListItem {
    id: string;
    date: string;
    level: string;
    step: string;
    message: string;
    details: string;
    raw: NovedadesExportServiceModel.ProcessEvent;
}

interface UseExportNovedadesEventsStepViewModelProps {
    processID: string;
    service: NovedadesExportService;
}

const levelLabels: Record<string, string> = {
    INFO: "Información",
    WARNING: "Aviso",
    ERROR: "Error",
};

const stepLabels: Record<string, string> = {
    EMPLOYEE_SCOPE: "Colaboradores",
    WORKED_HOURS: "Horas trabajadas",
    COMMON_NOVEDADES: "Novedades comunes",
    DAILY_DETAIL: "Detalle diario",
    SUMMARY: "Resumen",
    FORMAT_EXPORT: "Formato de exportación",
    SAVE_FILE: "Archivo",
    PREPARING_EXPORT_DATA: "Preparación de exportación",
    DATA_VALIDATION: "Validación de datos",
    FILE_GENERATION: "Generación de archivo",
    FILE_CREATED: "Archivo creado",
};

const formatTimestamp = (timestamp: number | undefined): string => {
    if (!timestamp) {
        return "N/D";
    }

    return new Date(timestamp).toLocaleString("es-ES", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatDetails = (details: unknown): string => {
    if (details == null) {
        return "N/D";
    }

    if (typeof details === "string") {
        return details;
    }

    try {
        return JSON.stringify(details);
    } catch {
        return "N/D";
    }
};

const mapEventToListItem = (
    event: NovedadesExportServiceModel.ProcessEvent,
): ExportNovedadesEventListItem => {
    return {
        id: event.eventID,
        date: formatTimestamp(event.timestamp ?? event.firestoreCreationTimestamp),
        level: levelLabels[event.level] ?? event.level,
        step: event.step ? (stepLabels[event.step] ?? event.step) : "N/D",
        message: event.message,
        details: formatDetails(event.details),
        raw: event,
    };
};

const timestampForSort = (event: NovedadesExportServiceModel.ProcessEvent): number => {
    return event.timestamp ?? event.firestoreCreationTimestamp ?? 0;
};

export const useExportNovedadesEventsStepViewModel = ({
    processID,
    service,
}: UseExportNovedadesEventsStepViewModelProps) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [events, setEvents] = useState<NovedadesExportServiceModel.ProcessEvent[]>([]);

    const refresh = useCallback(async () => {
        if (!processID) {
            setEvents([]);
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const nextEvents = await service.listEvents(processID);
            setEvents(nextEvents);
        } catch (loadError) {
            console.error(loadError);
            setError("No se pudieron cargar los eventos de la exportación.".localized());
        } finally {
            setLoading(false);
        }
    }, [processID, service]);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    const rows = useMemo(() => {
        return [...events]
            .sort((left, right) => timestampForSort(right) - timestampForSort(left))
            .map(mapEventToListItem);
    }, [events]);

    return {
        loading,
        error,
        rows,
        refresh,
    };
};
